import type { ChannelKind, UserStatus } from "@bundle/contracts";
import type { ReactNode } from "react";
import { Avatar, AvatarStack, Icon, IconButton, Pressable, Stack, Text } from "../primitives/index.ts";
import { useT, useTr } from "../i18n/I18nProvider.tsx";
import { HuddleChip } from "./huddle/HuddleChip.tsx";
import { StatusEmoji, useStatusDisplay } from "./StatusEmoji.tsx";

export type ChannelHeaderProps = {
  title: string;
  kind?: ChannelKind;
  topic?: string;
  memberCount: number;
  external: boolean;
  /** DMs: the other person (avatar + presence). */
  dmAvatar?: { name: string; src?: string; presence?: "online" | "away"; square?: boolean; /** Do not disturb: a moon. */ dnd?: boolean; /** Their custom status: emoji after the name, text and "until" under it (1:1 DMs). */ status?: UserStatus };
  /** A line under the title (the Support DM: "Answers right away…"); a custom status line takes its place. */
  subtitle?: string;
  /** Group DMs: the first people's faces (Slack's group DM picture); the members pill lists everyone. */
  groupAvatars?: { name: string; src?: string }[];
  canHuddle: boolean;
  onHuddle: () => void;
  /** The conversation's live huddle: shown as a chip (faces + Join, or "In huddle" when I'm in it) instead of the button. */
  huddle?: { faces: { name: string; src?: string; square?: boolean }[]; joined: boolean; summary: string };
  /** Channel settings (gear). Absent → no gear: a control is never shown without an action. */
  onSettings?: () => void;
  /** The members panel (member-count pill). Absent → no pill. */
  onMembers?: () => void;
  /** The Pinned messages button (MessageExtras.tsx PinnedButton), before the gear. Absent → none. */
  pins?: ReactNode;
  /** The channel's workflows menu (ChannelWorkflowsButton), before the pins. Absent → none. */
  workflows?: ReactNode;
  testID: string;
};

/** Channel title, topic, external marker, members pill and huddle / settings buttons (each only with its action). */
export function ChannelHeader(p: ChannelHeaderProps) {
  const t = useT();
  const tr = useTr();
  const isDm = p.kind === "dm" || p.kind === "group_dm";
  const status = useStatusDisplay(p.kind === "dm" ? p.dmAvatar : undefined);
  // "In a meeting · until 7:59 PM"; emoji only → "Until 7:59 PM"; no expiry → just the text.
  const statusLine =
    status === undefined ? "" : status.text === "" ? (status.until === undefined ? "" : tr(status.until)) : status.untilInline === undefined ? status.text : `${status.text} · ${tr(status.untilInline)}`;
  return (
    <Stack direction="horizontal" align="center" gap="2" paddingX="5" paddingY="2.5" testID={p.testID}>
      {p.groupAvatars !== undefined && p.groupAvatars.length > 0 ? (
        <AvatarStack people={p.groupAvatars} />
      ) : p.dmAvatar === undefined ? (
        <Icon name={p.kind === "private" ? "lock" : isDm ? "users" : "hash"} size="icon.md" color="text.secondary" />
      ) : (
        <Avatar name={p.dmAvatar.name} src={p.dmAvatar.src} presence={p.dmAvatar.presence} dnd={p.dmAvatar.dnd} dndLabel={t("dnd.paused")} square={p.dmAvatar.square} size="avatar.sm" />
      )}
      <Stack grow direction="horizontal" align="center" gap="2">
        {status === undefined && p.subtitle !== undefined ? (
          <Stack gap="0">
            <Text variant="headline" weight="semibold" lines={1} testID={`${p.testID}-title`}>
              {p.title}
            </Text>
            <Text variant="caption" color="text.tertiary" lines={1} testID={`${p.testID}-subtitle`}>
              {p.subtitle}
            </Text>
          </Stack>
        ) : status === undefined ? (
          <Text variant="headline" weight="semibold" lines={1} testID={`${p.testID}-title`}>
            {p.title}
          </Text>
        ) : (
          // A 1:1 DM with someone who set a status: the emoji after the name and the status under it (no hover needed).
          <Stack gap="0">
            <Stack direction="horizontal" align="center" gap="1.5">
              <Text variant="headline" weight="semibold" lines={1} testID={`${p.testID}-title`}>
                {p.title}
              </Text>
              <StatusEmoji person={p.dmAvatar} size="md" tooltip={false} testID={`${p.testID}-status-emoji`} />
            </Stack>
            {statusLine === "" ? null : (
              <Text variant="caption" color="text.secondary" lines={1} testID={`${p.testID}-status`}>
                {statusLine}
              </Text>
            )}
          </Stack>
        )}
        {p.external ? <Icon name="external" size="icon.sm" color="external.fg" /> : null}
        {p.topic === undefined || p.topic === "" ? null : (
          <Stack grow direction="horizontal" align="center" gap="2">
            <Text color="text.tertiary">·</Text>
            <Text variant="footnote" color="text.secondary" lines={1}>
              {p.topic}
            </Text>
          </Stack>
        )}
      </Stack>
      {p.kind === "dm" || p.onMembers === undefined ? null : (
        <Pressable direction="horizontal" radius="full" onPress={p.onMembers} accessibilityLabel={t("channel.members", { count: p.memberCount })} testID={`${p.testID}-members`}>
          <Stack direction="horizontal" align="center" gap="1.5" paddingX="2.5" paddingY="1" radius="full" border="border.subtle">
            <Icon name="users" size="icon.sm" color="text.secondary" />
            <Text variant="footnote" weight="medium" color="text.secondary">
              {String(p.memberCount)}
            </Text>
          </Stack>
        </Pressable>
      )}
      {p.canHuddle && p.huddle !== undefined ? (
        <HuddleChip
          faces={p.huddle.faces}
          joined={p.huddle.joined}
          label={p.huddle.joined ? t("huddle.inHuddle") : t("huddle.join")}
          accessibilityLabel={p.huddle.joined ? t("huddle.openWindow") : `${t("huddle.joinHuddle")}: ${p.huddle.summary}`}
          onPress={p.onHuddle}
          testID={`${p.testID}-huddle-chip`}
        />
      ) : p.canHuddle ? (
        <IconButton icon="huddle" accessibilityLabel={t("channel.huddle")} onPress={p.onHuddle} testID={`${p.testID}-huddle`} />
      ) : null}
      {p.workflows ?? null}
      {p.pins ?? null}
      {p.onSettings === undefined ? null : (
        <IconButton icon="settings" accessibilityLabel={t("channel.settings")} onPress={p.onSettings} testID={`${p.testID}-settings`} />
      )}
    </Stack>
  );
}
